import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { AppInfo } from '../environment';

type PostcodeLookupResponse = {
  Description: string;
  Highlight: string;
  Id: string;
  Text: string;
  Type: string;
  costType?: 'UK' | 'IRELAND' | 'UNKNOWN';
};

export namespace PostCode {
  let http: HttpClient | undefined;
  const loadedPostCodes: Record<string, any> = {};

  export function initialise(httpClient: HttpClient) {
    http = httpClient;
  }

  const callCostMessages: Record<string, string> = {
    standard:
      "Calls cost the basic rate per minute plus your phone company's access charge. From a mobile, they can cost much more. Calls to 0800 numbers are always free.",
    UK: "Calls cost the basic rate per minute plus your phone company's access charge. From a mobile, they can cost much more. Calls to 0800 numbers are always free.",
    IRELAND:
      'Calls cost up to 9 cents a minute (plus call set up fee of 29 cents). Except for 0818 numbers (which cost up to 5.25 cents a minute with a call set up fee of 9.66 cents) and 1800 numbers (which are free from all phones, whether mobile or landline).',
  };

  export function getCallCostMessagingForPostCode(postCode: string): Observable<string> {
    return new Observable((obs) => {
      if (!postCode) {
        obs.next(callCostMessages['standard']);
        return;
      }
      getCountryOfPostCode(postCode).subscribe({
        next: (country: string) => {
          return obs.next(callCostMessages[country] ?? callCostMessages['standard']);
        },
      });
    });
  }

  export function getCountryOfPostCode(
    postCode: string,
    id: string = '',
    countries: string[] = ['GB', 'IRL']
  ): Observable<'UK' | 'IRELAND' | 'UNKNOWN'> {
    if (!postCode) return of('UNKNOWN');

    return lookUpPostCodes(postCode, id, countries).pipe(
      map((value) => {
        return value[0].costType || 'UK';
      })
    );
  }

  export function lookup(postCode: string, id: string = '', countries: string[] = ['GB', 'IRL']) {
    if (!postCode) return of([]);

    if (loadedPostCodes[`${postCode}_${id}`]) return of(loadedPostCodes[`${postCode}_${id}`]);

    return lookUpPostCodes(postCode, id, countries);
  }

  function lookUpPostCodes(postCode: string, id: string = '', countries: string[] = ['GB', 'IRL']) {
    const { serviceFind, serviceKey } = AppInfo.Environment.postCodeLookupConfig;

    const queryParams = new HttpParams()
      .append('key', serviceKey)
      .append('Text', postCode)
      .append('Container', id)
      .append('Countries', countries.join(','))
      .append('Middleware', 'false');

    return http!
      .get<{ Items: PostcodeLookupResponse[] }>(serviceFind, {
        params: queryParams,
      })
      .pipe(
        map((value) => {
          return value.Items.map((item) => {
            item.costType = item.Id.startsWith('GB') ? 'UK' : item.Id.startsWith('IE') ? 'IRELAND' : 'UNKNOWN';
            return item;
          });
        }),
        tap((value) => {
          loadedPostCodes[`${postCode}_${id}`] = value;
        })
      );
  }
}
